import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ProgramApi } from "@/api/program.api";
import { TransactionApi } from "@/api/transaction.api";

import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  RotateCcw,
  CalendarDays,
  Filter,
  Download,
  ChevronDown,
  FileText,
  Loader2,
} from "lucide-react";
import {
  format,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { TransactionTableSkeleton } from "@/components/transactions/TransactionTableSkeleton";
import { useDebounce } from "@/hooks/use-debounce";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { downloadResponseFile } from "@/utils/common.utils";

const ROWS_OPTIONS = [5, 10, 20, 50];

function getDateRangeMillisPreset(
  range: string
): { startDate?: number; endDate?: number } | {} {
  const now = new Date();
  switch (range) {
    case "today":
      return {
        startDate: startOfDay(now).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "yesterday": {
      const y = subDays(now, 1);
      return {
        startDate: startOfDay(y).getTime(),
        endDate: endOfDay(y).getTime(),
      };
    }
    case "7days":
      return {
        startDate: startOfDay(subDays(now, 6)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "30days":
      return {
        startDate: startOfDay(subDays(now, 29)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "90days":
      return {
        startDate: startOfDay(subDays(now, 89)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "thisMonth":
      return {
        startDate: startOfMonth(now).getTime(),
        endDate: endOfMonth(now).getTime(),
      };
    case "lastMonth":
      return {
        startDate: startOfMonth(subMonths(now, 1)).getTime(),
        endDate: endOfMonth(subMonths(now, 1)).getTime(),
      };
    case "all":
    default:
      return {};
  }
}

function customDateRangeToMillis(startDate: string, endDate: string) {
  return {
    startDate: startOfDay(new Date(startDate)).getTime(),
    endDate: endOfDay(new Date(endDate)).getTime(),
  };
}

const AccountingEntry = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [dateRange, setDateRange] = useState("30days");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("program");
  const [exportPopoverOpen, setExportPopoverOpen] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const initialFilters = {
    program: "all",
    dateRange: "",
    amount: "",
    invoiceId: "",
    loyyalTxId: "",
    merchantTxId: "",
    accountName: "",
    accountType: "",
    entryType: "",
    startDate: undefined,
    endDate: undefined,
  };
  const [filters, setFilters] = useState(initialFilters);
  const [pendingFilterSelections, setPendingFilterSelections] =
    useState(initialFilters);
  const filterFields = [
    { key: "program", default: "all" },
    { key: "dateRange", default: "" },
    { key: "amount", default: "" },
    { key: "invoiceId", default: "" },
    { key: "merchantTxId", default: "" },
    { key: "accountName", default: "" },
    { key: "accountType", default: "" },
    { key: "entryType", default: "" },
    // add more if needed
  ];

  const activeFilterCount = filterFields.reduce(
    (count, { key, default: def }) =>
      filters[key] && filters[key] !== def ? count + 1 : count,
    0
  );

  // Pagination
  const totalPages = Math.ceil(totalEntries / rowsPerPage);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 400);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    ProgramApi.getProgramsFromDB().then((res: any) => {
      setPrograms(
        Array.isArray(res.data?.programVOs) ? res.data.programVOs : []
      );
    });
  }, []);

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line
  }, [page, rowsPerPage, filters]);

  const fetchEntries = async () => {
    setIsLoading(true);
    console.log(filters, searchInput, "filters---");

    try {
      let payload: any = {
        pageNumber: page,
        pageSize: rowsPerPage,
        sortOrder: "desc",
        sortBy: "createdDate",
        transactionType: "earn",
        programCode: filters.program !== "all" ? filters.program : null,
        transactionId: filters.loyyalTxId?.trim()
          ? filters.loyyalTxId.trim()
          : null,
        merchantTransactionId: filters.merchantTxId?.trim()
          ? filters.merchantTxId.trim()
          : null,
        invoiceId: filters.invoiceId?.trim() ? filters.invoiceId.trim() : null,
        amount: filters.amount?.trim() ? Number(filters.amount.trim()) : null,
        accountName: filters.accountName?.trim()
          ? filters.accountName.trim()
          : null,
        accountType: filters.accountType?.trim()
          ? filters.accountType.trim()
          : null,
        entryType: filters.entryType?.trim() ? filters.entryType.trim() : null,
      };
      if (filters.startDate && filters.endDate) {
        const customRange = customDateRangeToMillis(
          filters.startDate,
          filters.endDate
        );
        payload.startDate = customRange.startDate;
        payload.endDate = customRange.endDate;
      } else if (filters.dateRange) {
        payload = {
          ...payload,
          ...getDateRangeMillisPreset(filters.dateRange),
        };
      }
      const res = await TransactionApi.getAccountingEntries(payload);
      const data = Array.isArray(res.data?.accountingEntryVOs)
        ? res.data.accountingEntryVOs
        : [];
      // Map API response to table row structure
      const mapped = data.map((entry: any) => ({
        txId: entry.transactionId || "",
        date: entry.createdDate
          ? format(new Date(entry.createdDate), "yyyy-MM-dd hh:mm a")
          : "",
        program: entry.programVO?.programName || "",
        merchant: entry.merchantVO?.merchantName || "",
        accountCode: entry.accountVO?.accountCode || "",
        accountName: entry.accountVO?.accountName || "",
        accountType: entry.accountVO?.accountType || "",
        amount: entry.amount,
        currency: entry.currency,
        invoiceId: entry.invoiceId || "",
        entryType: entry.entryType || "",
      }));
      setEntries(mapped);
      setFilteredEntries(mapped);
      setTotalEntries(Number(res.data?.totalRecords) || mapped.length);
    } catch (e) {
      setEntries([]);
      setFilteredEntries([]);
      setTotalEntries(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setFilters(initialFilters);
    setPendingFilterSelections(initialFilters);
    setSelectedProgram("all");
    setDateRange("30days");
    setPage(0);
    setCustomDateRange(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    fetchEntries();
    toast.success("Filters reset");
  };

  const handleExport = async (format: "csv" | "pdf") => {
    if (format === "csv") {
      setIsExportingCSV(true);
      try {
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        let payload: any = {
          sortOrder: "desc",
          sortBy: "createdDate",
          transactionType: "earn",
          programCode: filters.program !== "all" ? filters.program : null,
          transactionId: filters.loyyalTxId?.trim()
            ? filters.loyyalTxId.trim()
            : null,
          merchantTransactionId: filters.merchantTxId?.trim()
            ? filters.merchantTxId.trim()
            : null,
          invoiceId: filters.invoiceId?.trim()
            ? filters.invoiceId.trim()
            : null,
          amount: filters.amount?.trim() ? Number(filters.amount.trim()) : null,
          accountName: filters.accountName?.trim()
            ? filters.accountName.trim()
            : null,
          accountType: filters.accountType?.trim()
            ? filters.accountType.trim()
            : null,
          entryType: filters.entryType?.trim()
            ? filters.entryType.trim()
            : null,
        };
        if (filters.startDate && filters.endDate) {
          const customRange = customDateRangeToMillis(
            filters.startDate,
            filters.endDate
          );
          payload.startDate = customRange.startDate;
          payload.endDate = customRange.endDate;
        } else if (filters.dateRange) {
          payload = {
            ...payload,
            ...getDateRangeMillisPreset(filters.dateRange),
          };
        }

        const response = await fetch(
          `${API_BASE}/accounting-entry/getAccountingEntryCSVFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to export CSV");
        }

        const filename = await downloadResponseFile(
          response,
          `accounting_entries_${new Date().toISOString().split("T")[0]}.csv`,
        );
        toast.success(`Accounting entries exported as ${filename} successfully!`);
      } catch (error) {
        console.error("Error exporting CSV:", error);
        toast.error("Failed to export accounting entries as CSV");
      } finally {
        setIsExportingCSV(false);
        setExportPopoverOpen(false);
      }
    } else if (format === "pdf") {
      setIsExportingPDF(true);
      try {
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        let payload: any = {
          sortOrder: "desc",
          sortBy: "createdDate",
          transactionType: "earn",
          programCode: filters.program !== "all" ? filters.program : null,
          transactionId: filters.loyyalTxId?.trim()
            ? filters.loyyalTxId.trim()
            : null,
          merchantTransactionId: filters.merchantTxId?.trim()
            ? filters.merchantTxId.trim()
            : null,
          invoiceId: filters.invoiceId?.trim()
            ? filters.invoiceId.trim()
            : null,
          amount: filters.amount?.trim() ? Number(filters.amount.trim()) : null,
          accountName: filters.accountName?.trim()
            ? filters.accountName.trim()
            : null,
          accountType: filters.accountType?.trim()
            ? filters.accountType.trim()
            : null,
          entryType: filters.entryType?.trim()
            ? filters.entryType.trim()
            : null,
        };
        if (filters.startDate && filters.endDate) {
          const customRange = customDateRangeToMillis(
            filters.startDate,
            filters.endDate
          );
          payload.startDate = customRange.startDate;
          payload.endDate = customRange.endDate;
        } else if (filters.dateRange) {
          payload = {
            ...payload,
            ...getDateRangeMillisPreset(filters.dateRange),
          };
        }

        const response = await fetch(
          `${API_BASE}/accounting-entry/generateAccountingEntriesPdfFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to export PDF");
        }

        const filename = await downloadResponseFile(
          response,
          `accounting_entries_${new Date().toISOString().split("T")[0]}.pdf`,
        );
        toast.success(`Accounting entries exported as ${filename} successfully!`);
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast.error("Failed to export accounting entries as PDF");
      } finally {
        setIsExportingPDF(false);
        setExportPopoverOpen(false);
      }
    }
  };

  useEffect(() => {
    setFilters((prev) => {
      if (prev.loyyalTxId === debouncedSearchInput) return prev;
      return { ...prev, loyyalTxId: debouncedSearchInput };
    });
    // eslint-disable-next-line
  }, [debouncedSearchInput]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Accounting Entry</h1>
        <p className="text-gray-500 mt-1">
          View accounting entries for loyalty programs
        </p>
      </div>
      {/* Filter section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1 flex gap-2 items-end">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder="Search by transaction ID..."
                className="pl-9"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <div className="w-64 h-10 flex items-center">
            <DatePickerWithRange
              date={customDateRange}
              setDate={(range) => {
                setCustomDateRange(range);
                if (range?.from && range?.to) {
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: "custom",
                    startDate: startOfDay(range.from).toISOString(),
                    endDate: endOfDay(range.to).toISOString(),
                  }));
                }
              }}
              placeholder="Custom Date Range"
            />
          </div>
          <Popover
            open={filterPopoverOpen}
            onOpenChange={(open) => {
              setFilterPopoverOpen(open);
              if (open) setPendingFilterSelections({ ...filters });
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant={activeFilterCount > 0 ? "default" : "outline"}
                className="flex items-center gap-2 px-3 py-2 text-sm"
                onClick={() => setFilterPopoverOpen(true)}
                size="sm"
              >
                <Filter className="h-4 w-4" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end">
              <div className="flex max-h-[320px] h-[320px]">
                <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                  {[
                    { label: "Program", value: "program" },
                    { label: "Date Range", value: "dateRange" },
                    { label: "Amount", value: "amount" },
                    { label: "Invoice ID", value: "invoiceId" },
                    {
                      label: "Merchant Transaction ID",
                      value: "merchantTxId",
                    },
                    { label: "Account Name", value: "accountName" },
                    { label: "Account Type", value: "accountType" },
                    { label: "Entry Type", value: "entryType" },
                  ].map((cat) => (
                    <button
                      key={cat.value}
                      className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                        activeCategory === cat.value
                          ? "bg-white shadow text-blue-600"
                          : "text-gray-700"
                      }`}
                      onClick={() => setActiveCategory(cat.value)}
                    >
                      {cat.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <button
                    className="text-[10px] text-gray-500 hover:underline mt-2"
                    onClick={() => {
                      setPendingFilterSelections(initialFilters);
                      setFilters(initialFilters);
                      setSearchInput("");
                      setCustomDateRange(undefined);
                    }}
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex-1 p-2 flex flex-col min-h-0">
                  {activeCategory === "program" && (
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                      {/* Place 'All Programs' at the top */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="program-filter"
                          checked={pendingFilterSelections.program === "all"}
                          onChange={() =>
                            setPendingFilterSelections((prev: any) => ({
                              ...prev,
                              program: "all",
                            }))
                          }
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-xs font-medium">
                          All Programs
                        </span>
                      </label>
                      {programs.map((program: any) => (
                        <label
                          key={program.programCode}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="program-filter"
                            checked={
                              pendingFilterSelections.program ===
                              program.programCode
                            }
                            onChange={() =>
                              setPendingFilterSelections((prev: any) => ({
                                ...prev,
                                program: program.programCode,
                              }))
                            }
                            className="form-radio h-4 w-4 text-blue-600"
                          />
                          <span className="text-xs font-medium">
                            {program.programName}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {activeCategory === "dateRange" && (
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                      {[
                        { value: "30days", label: "Last 30 Days" },
                        { value: "7days", label: "Last 7 Days" },
                        { value: "today", label: "Today" },
                        { value: "yesterday", label: "Yesterday" },
                        { value: "thisMonth", label: "This Month" },
                        { value: "lastMonth", label: "Last Month" },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="dateRange-filter"
                            checked={
                              pendingFilterSelections.dateRange === opt.value
                            }
                            onChange={() =>
                              setPendingFilterSelections((prev: any) => ({
                                ...prev,
                                dateRange: opt.value,
                              }))
                            }
                            className="form-radio h-4 w-4 text-blue-600"
                          />
                          <span className="text-xs font-medium">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                      {false}
                    </div>
                  )}
                  {activeCategory === "amount" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="number"
                        placeholder="Enter amount"
                        value={pendingFilterSelections.amount}
                        onChange={(e) =>
                          setPendingFilterSelections((prev: any) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                    </div>
                  )}
                  {activeCategory === "invoiceId" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Enter Invoice ID"
                        value={pendingFilterSelections.invoiceId}
                        onChange={(e) =>
                          setPendingFilterSelections((prev: any) => ({
                            ...prev,
                            invoiceId: e.target.value,
                          }))
                        }
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                    </div>
                  )}
                  {activeCategory === "merchantTxId" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Enter Merchant Transaction ID"
                        value={pendingFilterSelections.merchantTxId}
                        onChange={(e) =>
                          setPendingFilterSelections((prev: any) => ({
                            ...prev,
                            merchantTxId: e.target.value,
                          }))
                        }
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                    </div>
                  )}
                  {activeCategory === "accountName" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Enter Account Name"
                        value={pendingFilterSelections.accountName}
                        onChange={(e) =>
                          setPendingFilterSelections((prev: any) => ({
                            ...prev,
                            accountName: e.target.value,
                          }))
                        }
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                    </div>
                  )}
                  {activeCategory === "accountType" && (
                    <div className="flex flex-col gap-2">
                      <select
                        value={pendingFilterSelections.accountType}
                        onChange={(e) =>
                          setPendingFilterSelections((prev: any) => ({
                            ...prev,
                            accountType: e.target.value,
                          }))
                        }
                        className="h-10 w-full border rounded px-2 text-sm"
                      >
                        <option value="">Select Account Type</option>
                        <option value="ASSET">Asset</option>
                        <option value="LIABILITY">Liability</option>
                        <option value="EQUITY">Equity</option>
                      </select>
                    </div>
                  )}
                  {activeCategory === "entryType" && (
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="entryType-filter"
                          checked={
                            pendingFilterSelections.entryType === "DEBIT"
                          }
                          onChange={() =>
                            setPendingFilterSelections((prev: any) => ({
                              ...prev,
                              entryType: "DEBIT",
                            }))
                          }
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-xs font-medium">Debit</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="entryType-filter"
                          checked={
                            pendingFilterSelections.entryType === "CREDIT"
                          }
                          onChange={() =>
                            setPendingFilterSelections((prev: any) => ({
                              ...prev,
                              entryType: "CREDIT",
                            }))
                          }
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-xs font-medium">Credit</span>
                      </label>
                    </div>
                  )}
                  <div className="flex justify-end mt-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-3 py-1 h-7"
                      onClick={() => {
                        setPendingFilterSelections(initialFilters);
                        setFilters(initialFilters);
                        setSearchInput("");
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs px-3 py-1 h-7"
                      onClick={() => {
                        setFilters({
                          ...pendingFilterSelections,
                          loyyalTxId: filters.loyyalTxId,
                        });
                        setSelectedProgram(
                          pendingFilterSelections.program || "all"
                        );
                        setDateRange(pendingFilterSelections.dateRange || "");
                        setFilterPopoverOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 px-3 py-2 text-sm"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-3 py-2 text-sm"
                disabled={isExportingCSV || isExportingPDF}
              >
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end">
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start text-sm"
                  onClick={() => handleExport("csv")}
                  disabled={isExportingCSV || isExportingPDF}
                >
                  {isExportingCSV ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  <span>CSV</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start text-sm"
                  onClick={() => handleExport("pdf")}
                  disabled={isExportingCSV || isExportingPDF}
                >
                  {isExportingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  <span>PDF</span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {/* Data Table */}
      {isLoading ? (
        <TransactionTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <DataTable
          title="Accounting Entries"
          data={filteredEntries}
          columns={[
            { header: "Date", accessorKey: "date" },
            { header: "Txn ID", accessorKey: "txId" },
            {
              header: "Program",
              accessorKey: "program",
              cell: (row: any) => {
                if (!row.program) return "";
                const words = row.program.split(" ");
                const display =
                  words.length > 2
                    ? `${words.slice(0, 2).join(" ")}...`
                    : row.program;
                return <span title={row.program}>{display}</span>;
              },
            },
            { header: "Merchant", accessorKey: "merchant" },
            { header: "Account Code", accessorKey: "accountCode" },
            {
              header: "Account Name / Type",
              id: "accountNameType",
              cell: (row: any) => (
                <div>
                  <div>{row.accountName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {row.accountType}
                  </div>
                </div>
              ),
            },
            { header: "Entry Type", accessorKey: "entryType" },
            {
              header: "Amount",
              id: "amountCurrency",
              cell: (row: any) => (
                <span className="inline-flex items-center justify-center rounded-full text-xs font-semibold w-16 h-10">
                  <span className="inline-block p-1">
                    {Number(row.amount).toFixed(1)}
                  </span>
                  <span>{row.currency}</span>
                </span>
              ),
            },
            { header: "Invoice ID", accessorKey: "invoiceId" },
          ]}
          customTableBody={undefined}
        />
      )}
      <style>{`
        .card-modern table tbody tr td { font-size: 0.85rem; }
      `}</style>
      {/* Pagination controls */}
      {!isLoading && totalEntries > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <EnhancedPaginationControl
              totalPages={totalPages}
              currentPage={page}
              onPageChange={(page) => setPage(page)}
            />
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingEntry;
